import React, { useState, useEffect, useMemo } from "react";
import Layout from "../../layouts";
import { toast } from "react-toastify";
import DynamicTable from "../../components/DynamicTable";
import apiService from "../../services/apiService";
import { withSearchIcon } from "../../utils/helper";

export default function CustomFeeDetails() {
  const [feeDetails, setFeeDetails] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Form State
  const [users, setUsers] = useState<any[]>([]);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [hostFee, setHostFee] = useState("");
  const [handlingFee, setHandlingFee] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);

  const pageSize = 50;

  // ✅ Table Columns with Edit Action
  const columns = [
    { key: "userId", label: "User ID" },
    { key: "first_name", label: withSearchIcon("First Name") },
    { key: "last_name", label: withSearchIcon("Last Name") },
    { key: "email", label: withSearchIcon("Email") },
    { key: "phone_no", label: "Phone Number" },
    {
      key: "host_fee",
      label: "Fixed Fee ($)",
      render: (val: number) => (val != null ? `$${val}` : "—"),
      isDecimal: true,
    },
    {
      key: "handling_fee",
      label: "Handling Fee (%)",
      render: (val: number) => (val != null ? `${val}%` : "—"),
    },

    {
      key: "actions",
      label: "Actions",
      className:
        "px-4 py-3 whitespace-nowrap text-right sticky right-0 z-10 rounded space-x-2 bg-white/30 backdrop-blur-md shadow-md",
      render: (_: any, row: any) => (
        <div className="flex gap-2">
          <button
            onClick={() => openEditModal(row)}
            className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
          >
            Edit
          </button>
        </div>
      ),
    },
  ];

  // ✅ Fetch Fee Details

  useEffect(() => {
    fetchFeeDetails(page, search);
  }, [page, search, refreshKey]);
  const fetchFeeDetails = async (activePageNo: number, search?: any) => {
    try {
      setLoading(true);
      const params = { page: activePageNo, limit: pageSize, search: search };
      const response: any = await apiService.get(
        "/fees/get-fee-details",
        params
      );
      setFeeDetails(response?.data?.data || []);
      setTotalPages(response?.data?.totalPages || 1);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error("Error fetching fee details:", error);
      toast.error("Failed to fetch fee details");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Search Filter

  // ✅ User Search
  const handleUserSearch = async (query: string) => {
    setUserSearch(query);
    if (!query.trim()) {
      setUsers([]);
      setSelectedUser(null);
      setHostFee("");
      setHandlingFee("");
      return;
    }
    if (query.length < 2) {
      setUsers([]);
      return;
    }
    try {
      const res: any = await apiService.get("/users/get-users", {
        search: query,
        limit: 1000,
      });
      setUsers(res?.data?.users || []);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  // ✅ When selecting user, fetch fee details for that user
  const handleSelectUser = async (user: any) => {
    setSelectedUser(user);
    setUserSearch(`${user.first_name} ${user.last_name}`);
    setUsers([]);
    try {
      const res: any = await apiService.get("/fees/get-fee-details-by-user", {
        userId: user.id,
      });
      if (res?.data) {
        setHostFee(res.data.host_fee?.toString() || "");
        setHandlingFee(res.data.handling_fee?.toString() || "");
      } else {
        setHostFee("");
        setHandlingFee("");
      }
    } catch (err) {
      console.error("Error fetching user fee details:", err);
      toast.error("Failed to load user fee details");
    }
  };

  // ✅ Handle Add/Edit Submission
  const handleSaveCustomFee1 = async () => {
    if (!selectedUser) return toast.error("Please select a user.");
    if (!hostFee || !handlingFee)
      return toast.error("All fields are required.");

    console.log("Selected User:", selectedUser);
    const payload = {
      userId: selectedUser.userId || selectedUser.id,
      hostFee: parseFloat(hostFee),
      handlingFee: parseFloat(handlingFee),
    };

    try {
      setSubmitting(true);
      if (isEditMode) {
        await apiService.put("/fees/update-custom-fee", payload);
        toast.success("Custom fee updated successfully!");
      } else {
        await apiService.put("/fees/update-custom-fee", payload);
        toast.success("Custom fee added successfully!");
      }

      setShowModal(false);
      resetModal();
      // setSearch("");
      if (!isEditMode) {
        setSearch("");
      }
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      console.error("Error saving custom fee:", err);
      toast.error("Failed to save custom fee");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveCustomFee = async () => {
    if (!selectedUser) return toast.error("Please select a user.");

    const hostValue = parseFloat(hostFee);
    const handlingValue = parseFloat(handlingFee);

    // Offline validation for zero, negative, or invalid values
    if (!hostValue || hostValue <= 0) {
      return toast.error("Fixed Fee must be greater than 0.");
    }

    if (!handlingValue || handlingValue <= 0) {
      return toast.error("Handling Fee must be greater than 0.");
    }

    const payload = {
      userId: selectedUser.userId || selectedUser.id,
      hostFee: Number(hostValue.toFixed(2)),
      handlingFee: Number(handlingValue.toFixed(2)),
    };

    try {
      setSubmitting(true);
      await apiService.put("/fees/update-custom-fee", payload);

      toast.success(
        isEditMode
          ? "Custom fee updated successfully!"
          : "Custom fee added successfully!"
      );

      setShowModal(false);
      resetModal();
      if (!isEditMode) setSearch("");
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      console.error("Error saving custom fee:", err);
      toast.error("Failed to save custom fee");
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ Open Edit Modal
  const openEditModal = (row: any) => {
    setIsEditMode(true);
    setSelectedUser(row);
    setHostFee(row.host_fee?.toString() || "");
    setHandlingFee(row.handling_fee?.toString() || "");
    setShowModal(true);
  };

  // ✅ Reset modal state
  const resetModal = () => {
    setSelectedUser(null);
    setHostFee("");
    setHandlingFee("");
    setUserSearch("");
    setUsers([]);
    setIsEditMode(false);
  };

  return (
    <Layout
      pageTitle="Host Fee (Custom)"
      actionButton={{
        label: "Add Host Fee",
        onClick: () => {
          resetModal();
          setShowModal(true);
        }, // 👈 your custom handler
        className: "bg-green-600 hover:bg-green-700",
      }}
    >
      <div className="min-h-screen bg-gray-50 py-2 px-4">
        {/* Table */}

        <DynamicTable
          title="Host Fee"
          data={feeDetails}
          columns={columns}
          pageSize={pageSize}
          loading={loading}
          totalRecords={totalRecords}
          totalPages={totalPages}
          activeNUmber={currentPage}
          onPageChange={(page) => {
            setCurrentPage(page);
            setPage(page);
          }}
          onSearch={(val?: any) => {
            setPage(1);
            setSearch(val);
          }}
          searchKey={search}
        />

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-lg">
              <h3 className="text-lg font-semibold mb-4">
                {isEditMode ? "Edit Custom Fee" : "Add Custom Fee"}
              </h3>

              {/* User Search */}
              {!isEditMode && (
                <div className="mb-3">
                  <label className="block text-sm font-medium mb-1">
                    Search User
                  </label>
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => handleUserSearch(e.target.value)}
                    placeholder="Type user name..."
                    className="border rounded-lg w-full px-3 py-2"
                  />
                  {users.length > 0 && (
                    <ul className="border rounded-lg mt-2 max-h-40 overflow-auto">
                      {users.map((user: any) => (
                        <li
                          key={user.id}
                          onClick={() => handleSelectUser(user)}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                        >
                          {user.first_name} {user.last_name} ({user.email})
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Prefilled Fields */}
              {selectedUser && (
                <>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-sm font-medium">First Name</label>
                      <input
                        type="text"
                        disabled
                        value={selectedUser.first_name || ""}
                        className="border rounded-lg w-full px-3 py-2 bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Last Name</label>
                      <input
                        type="text"
                        disabled
                        value={selectedUser.last_name || ""}
                        className="border rounded-lg w-full px-3 py-2 bg-gray-100"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-sm font-medium">Email</label>
                      <input
                        type="text"
                        disabled
                        value={selectedUser.email || ""}
                        className="border rounded-lg w-full px-3 py-2 bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">
                        Phone Number
                      </label>
                      <input
                        type="text"
                        disabled
                        value={selectedUser.phone_no || ""}
                        className="border rounded-lg w-full px-3 py-2 bg-gray-100"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Fee Inputs */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="text-sm font-medium">Fixed Fee ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={hostFee}
                    onChange={(e) => {
                      // Allow only whole numbers
                      const val = e.target.value;
                      setHostFee(val);
                    }}
                    placeholder="e.g. 3"
                    className="border rounded-lg w-full px-3 py-2"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">
                    Handling Fee (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={handlingFee}
                    onChange={(e) => setHandlingFee(e.target.value)}
                    placeholder="e.g. 0.3"
                    className="border rounded-lg w-full px-3 py-2"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetModal();
                  }}
                  className="px-4 py-2 rounded-lg border hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCustomFee}
                  disabled={submitting}
                  className={`px-4 py-2 rounded-lg text-white ${
                    submitting
                      ? "bg-indigo-300 cursor-not-allowed"
                      : "bg-indigo-600 hover:bg-indigo-700"
                  }`}
                >
                  {submitting
                    ? "Saving..."
                    : isEditMode
                    ? "Update Fee"
                    : "Save Fee"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
